import React, { useState, useEffect, useRef } from "react";
import { FaCheckCircle } from "react-icons/fa";
import ShowcaseSections from "../ShowcaseSections";
import CategoryCustomization from "../CategoryCustomization";
import { getGalleryImages, getThemeColor, saveGalleryImages, updateThemeColor, saveSearchNames, getSearchNames } from "../../services/ClientService";
import { FiTrash2 } from "react-icons/fi";
import { uploadImageToS3Bucket, optimizeAndConvertToWebp } from "../../services/imageOptimizerService";
import { deleteImageFromS3 } from "../../services/ClientService";
import { MdOutlineColorLens } from "react-icons/md";
import { GrGallery } from "react-icons/gr";

const hexToHsl = (hex) => {
    const r = parseInt(hex.slice(1, 3), 16) / 255;
    const g = parseInt(hex.slice(3, 5), 16) / 255;
    const b = parseInt(hex.slice(5, 7), 16) / 255;

    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h, s, l;
    l = (max + min) / 2;

    if (max === min) {
        h = s = 0; // Achromatic
    } else {
        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

        switch (max) {
            case r:
                h = (g - b) / d + (g < b ? 6 : 0);
                break;
            case g:
                h = (b - r) / d + 2;
                break;
            case b:
                h = (r - g) / d + 4;
                break;
            default:
                break;
        }
        h *= 60;
    }

    return { h: Math.round(h), s: Math.round(s * 100), l: Math.round(l * 100) };
};

const hslToHex = (h, s, l) => {
    s /= 100;
    l /= 100;

    const c = (1 - Math.abs(2 * l - 1)) * s;
    const x = c * (1 - Math.abs((h / 60) % 2 - 1));
    const m = l - c / 2;
    let r = 0,
        g = 0,
        b = 0;

    if (0 <= h && h < 60) {
        r = c;
        g = x;
        b = 0;
    } else if (60 <= h && h < 120) {
        r = x;
        g = c;
        b = 0;
    } else if (120 <= h && h < 180) {
        r = 0;
        g = c;
        b = x;
    } else if (180 <= h && h < 240) {
        r = 0;
        g = x;
        b = c;
    } else if (240 <= h && h < 300) {
        r = x;
        g = 0;
        b = c;
    } else if (300 <= h && h < 360) {
        r = c;
        g = 0;
        b = x;
    }

    r = Math.round((r + m) * 255).toString(16).padStart(2, "0");
    g = Math.round((g + m) * 255).toString(16).padStart(2, "0");
    b = Math.round((b + m) * 255).toString(16).padStart(2, "0");

    return `#${r}${g}${b}`;
};

const HomeCustomizations = () => {
    const [primaryColor, setPrimaryColor] = useState("#ededed");
    const [isColorsSaved, setIsColorsSaved] = useState(false);
    const [galleryImages, setGalleryImages] = useState([]);
    const [deletedIds, setDeletedIds] = useState([]);
    const [galleryChanged, setGalleryChanged] = useState(false);
    const [loading, setLoading] = useState(true); // Add loading state
    const [searchVariables, setSearchVariables] = useState([]);
    const [searchInput, setSearchInput] = useState("");
    const [isSearchVariablesSaved, setIsSearchVariablesSaved] = useState(false);
    const fileInputRef = useRef(null);
    const calculateShades = (hex) => {
        const hsl = hexToHsl(hex);
        return {
            // Lightened shades
            shade1: hslToHex(hsl.h, hsl.s, Math.min(hsl.l + 10, 100)), // Lightens by 10%
            shade2: hslToHex(hsl.h, hsl.s, Math.min(hsl.l + 20, 100)), // Lightens by 20%
            shade3: hslToHex(hsl.h, hsl.s, Math.min(hsl.l + 30, 100)), // Lightens by 12% (60% less intense than 30%)
            shade4: hslToHex(hsl.h, hsl.s, Math.min(hsl.l + 66, 100)), // Lightens by 16% (60% less intense than 40%)
            // Darkened shades
            darkShade1: hslToHex(hsl.h, hsl.s, Math.max(hsl.l - 10, 0)), // Darkens by 10%
            darkShade2: hslToHex(hsl.h, hsl.s, Math.max(hsl.l - 20, 0)), // Darkens by 20%
            darkShade3: hslToHex(hsl.h, hsl.s, Math.max(hsl.l - 30, 0)), // Darkens by 30%
            darkShade4: hslToHex(hsl.h, hsl.s, Math.max(hsl.l - 40, 0)), // Darkens by 40%
        };
    };

    useEffect(() => {
        const initializeData = async () => {
            setLoading(true);
            try {
                await fetchColorData({});
                const res = await getGalleryImages({});
                if (res.success && Array.isArray(res.data)) {
                    const images = res.data.map((item) => ({
                        _id: item._id,
                        preview: item.imageUrl,
                        fileName: item.fileName,
                        fileType: item.fileType,
                        isPending: false,
                        file: null,
                    }));
                    setGalleryImages(images);
                }
                // Fetch saved search names
                const searchRes = await getSearchNames({});
                if (searchRes.success && searchRes.data?.searchNames?.length > 0) {
                    setSearchVariables(searchRes.data.searchNames);
                    setIsSearchVariablesSaved(true);
                }
            } catch (err) {
                console.error("Error initializing data:", err);
            } finally {
                setLoading(false);
            }
        };
        initializeData();
    }, []);

    useEffect(() => {
        return () => {
            galleryImages.forEach((img) => {
                if (img.isPending) {
                    URL.revokeObjectURL(img.preview);
                }
            });
        };
    }, [galleryImages]);

    const fetchColorData = async (params) => {
        try {
            const data = await getThemeColor(params);
            if (data.success && data.data && data.data.primaryColor) {
                setPrimaryColor(data.data.primaryColor);
            }
        } catch (error) {
            console.error("Error fetching branch data:", error);
        }
    };

    const handleColorsSave = async () => {
        try {
            const shades = calculateShades(primaryColor);
            const colorData = {
                primaryColor,
                shades: {
                    shade1: shades.shade1,
                    shade2: shades.shade2,
                    shade3: shades.shade3,
                    shade4: shades.shade4,
                    darkShade1: shades.darkShade1,
                    darkShade2: shades.darkShade2,
                    darkShade3: shades.darkShade3,
                    darkShade4: shades.darkShade4,
                },
            };
            const data = await updateThemeColor(colorData);
            if (data.success) setIsColorsSaved(true);
        } catch (error) {
            console.error("Error saving color data:", error);
        }
    };

    const handleColorChange = (e) => {
        setPrimaryColor(e.target.value);
        setIsColorsSaved(false);
    };

    useEffect(() => {
        console.log("Updated gallery images:", galleryImages);
    }, [galleryImages]);

    const addFiles = async (files) => {
        const newImages = [];
        for (const file of files) {
            try {
                const reader = new FileReader();
                const previewPromise = new Promise((resolve, reject) => {
                    reader.onloadend = () => resolve(reader.result);
                    reader.onerror = reject;
                    reader.readAsDataURL(file);
                });
                const preview = await previewPromise;
                const optimizedBlob = await optimizeAndConvertToWebp(file);
                newImages.push({ preview, file: optimizedBlob, isPending: true, fileName: null, fileType: null });
            } catch (error) {
                console.error("Error processing image:", error);
            }
        }
        setGalleryImages(prev => [...prev, ...newImages]);
        setGalleryChanged(true); // enable save
        if (fileInputRef.current) fileInputRef.current.value = null;
    };



    // Function for handling file selection via the file input
    const handleFileSelect = async (e) => {
        const files = Array.from(e.target.files);
        if (files.length) await addFiles(files);
    };

    const handleGalleryImagesSave = async () => {
        if (!galleryChanged) return;
        try {
            // Upload pending files
            const pending = galleryImages.filter(img => img.isPending);
            const uploads = await Promise.all(
                pending.map(async (img) => {
                    const fileName = `zun${Date.now()}.webp`;
                    const fileType = "image/webp";
                    const url = await uploadImageToS3Bucket(img.file, fileType, fileName);
                    URL.revokeObjectURL(img.preview);
                    return { imageUrl: url, fileName, fileType };
                })
            );
            // Call API
            await saveGalleryImages({ deleted: deletedIds, uploaded: uploads });
            // 3) Reset deletedIds and reload from backend
            setDeletedIds([]);
            const fresh = await getGalleryImages({});
            if (fresh.success) {
                const imgs = fresh.data.map((it) => ({
                    _id: it._id,
                    preview: it.imageUrl,
                    fileName: it.fileName,
                    fileType: it.fileType,
                    isPending: false,
                    file: null,
                }));
                setGalleryImages(imgs);
            }
            setGalleryChanged(false); // disable save
        } catch (err) {
            console.error("Error saving gallery images:", err);
        }
    };

    const handleRemoveImage = (index) => {
        const img = galleryImages[index];
        // don't delete immediately, mark for deletion
        setGalleryImages(prev => prev.filter((_, i) => i !== index));
        if (!img.isPending && img._id) {
            setDeletedIds(prev => [...prev, img._id]);
        }
        if (img.isPending) URL.revokeObjectURL(img.preview);
        setGalleryChanged(true); // enable save
    };

    const handleDrop = (e) => {
        e.preventDefault();
        const files = Array.from(e.dataTransfer.files);
        addFiles(files);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
    };

    // Handler for adding a variable
    const handleAddSearchVariable = (e) => {
        e.preventDefault();
        const value = searchInput.trim();
        if (value && !searchVariables.includes(value)) {
            setSearchVariables(prev => [...prev, value]);
            setIsSearchVariablesSaved(false);
        }
        setSearchInput("");
    };

    // Handler for removing a variable
    const handleRemoveSearchVariable = (index) => {
        setSearchVariables(prev => prev.filter((_, i) => i !== index));
        setIsSearchVariablesSaved(false);
    };

    // Handler for saving variables
    const handleSaveSearchVariables = async () => {
        try {
            const res = await saveSearchNames({ searchNames: searchVariables });
            if (res.success) {
                setIsSearchVariablesSaved(true);
            }
        } catch (error) {
            console.error("Error saving search names:", error);
        }
    };

    return (
        <div className="bg-gradient-to-r from-indigo-200 to-blue-100 min-h-screen">
            {loading ? (
                <div className="fixed inset-0 flex justify-center items-center bg-white/80 z-50">
                    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
                </div>
            ) : (
                <div className="bg-white p-6 max-md:px-5 space-y-8">
                    {/* Theme Customization */}
                    <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700">
                        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-2">
                            <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 flex items-center gap-3">
                                <MdOutlineColorLens /> Theme Customization
                                {isColorsSaved && (
                                    <span className="flex items-center text-green-600 dark:text-green-400 text-sm font-medium">
                                        <FaCheckCircle className="w-5 h-5 mr-1.5" />
                                        Saved
                                    </span>
                                )}
                            </h2>
                        </div>
                        <div className="space-y-4 md:space-y-6">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Primary Color
                                </label>
                                <div className="flex flex-col md:flex-row gap-4 md:items-center">
                                    <input
                                        type="color"
                                        value={primaryColor}
                                        onChange={handleColorChange}
                                        className="w-full h-12 cursor-pointer rounded-lg border-2 border-gray-300 dark:border-gray-600 p-1 bg-white dark:bg-gray-700"
                                        aria-label="Select primary color"
                                    />
                                    <button
                                        onClick={handleColorsSave}
                                        className="md:w-48 px-6 py-3 rounded-full font-semibold text-white bg-green-600 hover:bg-green-700 dark:bg-green-700 dark:hover:bg-green-600 transition-all duration-200 transform hover:scale-[1.02] shadow-sm"
                                    >
                                        Save Changes
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Add Search Variables Multi-Select */}
                    <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700">
                        <div className="flex items-center gap-2 mb-2">
                            <label className="block text-lg font-medium text-gray-700 dark:text-gray-300">
                                Add Search Names
                            </label>
                            {isSearchVariablesSaved && (
                                <span className="flex items-center text-green-600 dark:text-green-400 text-sm font-medium">
                                    <FaCheckCircle className="w-5 h-5 mr-1.5" />
                                    Saved
                                </span>
                            )}
                        </div>
                        <form
                            onSubmit={handleAddSearchVariable}
                            className="flex gap-2"
                        >
                            <input
                                type="text"
                                value={searchInput}
                                onChange={e => setSearchInput(e.target.value)}
                                className="flex-1 rounded-lg border border-gray-300 dark:border-gray-600 px-3 py-2 bg-white dark:bg-gray-700"
                                placeholder="Type and press Enter or click Create"
                            />
                            <button
                                type="submit"
                                className="px-4 py-2 rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700 transition"
                            >
                                Create
                            </button>
                        </form>
                        {/* Show selected variables as chips */}
                        {searchVariables.length > 0 && (
                            <div className="flex flex-wrap gap-2 mt-3">
                                {searchVariables.map((item, idx) => (
                                    <span
                                        key={idx}
                                        className="flex items-center bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm"
                                    >
                                        {item}
                                        <button
                                            type="button"
                                            className="ml-2 text-blue-500 hover:text-red-500"
                                            onClick={() => handleRemoveSearchVariable(idx)}
                                            aria-label="Remove"
                                        >
                                            &times;
                                        </button>
                                    </span>
                                ))}
                            </div>
                        )}
                        <button
                            onClick={handleSaveSearchVariables}
                            className="mt-4 px-5 py-2 rounded-full font-semibold text-white bg-green-600 hover:bg-green-700 transition"
                        >
                            Save Search Names
                        </button>
                    </div>

                    {/* Section Customization */}
                    <ShowcaseSections />

                    {/* Category Customization */}
                    <CategoryCustomization />

                    {/* Gallery Customization */}
                    <div className="w-full bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700">
                        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-2">
                            <div className="flex items-center gap-2">
                                <GrGallery />
                                <p className="text-lg font-semibold text-gray-800 dark:text-gray-100">
                                    Upload Gallery Images
                                </p>
                            </div>
                            <button
                                onClick={handleGalleryImagesSave}
                                disabled={!galleryChanged}
                                className={`px-5 py-2 rounded-full font-semibold text-white transition-all duration-200 transform hover:scale-[1.02] shadow-sm ${galleryChanged ? 'bg-green-600 hover:bg-green-700 dark:bg-green-700 dark:hover:bg-green-600' : 'bg-gray-400 cursor-not-allowed'}`}
                            >
                                Save Changes
                            </button>
                        </div>
                        <div
                            onDrop={handleDrop}
                            onDragOver={handleDragOver}
                            onClick={() => fileInputRef.current && fileInputRef.current.click()}
                            className="border-2 border-dashed border-gray-300 dark:border-gray-600 p-6 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-blue-500 transition-colors"
                        >
                            <div className="py-6 text-center">
                                <p className="text-gray-500 mb-2">Click to upload images</p>
                                <p className="text-xs text-gray-400">Drag &amp; drop images here</p>
                            </div>
                            <input
                                type="file"
                                ref={fileInputRef}
                                multiple
                                accept="image/*"
                                onChange={handleFileSelect}
                                className="hidden"
                                id="gallery-upload"
                            />
                        </div>

                        {galleryImages.length > 0 && (
                            <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                                {galleryImages.map((img, index) => (
                                    <div key={index} className="relative group">
                                        {!img.preview ? (
                                            <div className="w-full h-32 flex items-center justify-center bg-gray-100 rounded-lg">
                                                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
                                            </div>
                                        ) : (
                                            <>
                                                <img
                                                    src={img.preview}
                                                    alt={`Gallery ${index}`}
                                                    className="w-full h-32 object-cover rounded-lg shadow"
                                                />
                                                <button
                                                    onClick={() => handleRemoveImage(index)}
                                                    className="absolute top-1 right-1 bg-white bg-opacity-75 rounded-full p-1 hover:bg-opacity-100 shadow-lg"
                                                >
                                                    <FiTrash2 className="w-4 h-4 text-red-500" />
                                                </button>
                                            </>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default HomeCustomizations;
